import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfigFile from "../css/tailwind.config.js";
import axios from "axios";
import * as XLSX from "xlsx/xlsx.mjs";
import moment from "moment";

export const tailwindConfig = () => {
  // Tailwind config
  return resolveConfig(tailwindConfigFile);
};

export const hexToRGB = (h) => {
  let r = 0;
  let g = 0;
  let b = 0;
  if (h.length === 4) {
    r = `0x${h[1]}${h[1]}`;
    g = `0x${h[2]}${h[2]}`;
    b = `0x${h[3]}${h[3]}`;
  } else if (h.length === 7) {
    r = `0x${h[1]}${h[2]}`;
    g = `0x${h[3]}${h[4]}`;
    b = `0x${h[5]}${h[6]}`;
  }
  return `${+r},${+g},${+b}`;
};

export const formatValue = (value) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumSignificantDigits: 3,
    notation: "compact",
  }).format(value);

export const formatThousands = (value) =>
  Intl.NumberFormat("en-US", {
    maximumSignificantDigits: 3,
    notation: "compact",
  }).format(value);

export const logger = (...args) => {
  if (process.env.NODE_ENV === "development") {
    console.log(...args);
  }
};

export const getDataAxios = async (url) =>
  await axios.get(url, { headers: HEADER }).then((res) => res);

export const HEADER = {
  "Content-Type": "application/json",
  "X-Kartini-Signature": "a2FydGluaUAyMDIy",
};

export const formatPhoneNumberIndonesia = (phoneNumber) => {
  // Remove all non-numeric characters from the input string
  phoneNumber = phoneNumber.replace(/\D/g, "");

  // Check if the phone number starts with a valid prefix
  const prefixRegex = /^(62)/;
  if (prefixRegex.test(phoneNumber)) {
    // If the prefix is missing or invalid, return the original input
    return phoneNumber;
  }

  // Remove the leading "0" and "62" if the number starts with it
  phoneNumber = phoneNumber.replace(/^(0|\+)/, "");

  // Add the country code "62" to the phone number
  phoneNumber = "62" + phoneNumber;

  return phoneNumber;
};

export const isValidEmail = (email) => {
  // Define a regular expression pattern for a simple email validation
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test the email against the pattern and return the result
  return pattern.test(email);
};

/**
 * Exports data to an Excel file.
 *
 * @param {string} fileName - The name of the Excel file to be exported.
 * @param {Array} data - The data to be exported to the Excel file.
 */
export const ExportToExcel = (fileName, data) => {
  let ws = XLSX.utils.json_to_sheet(data);
  let wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "sheet");
  XLSX.writeFile(
    wb,
    `${fileName}-${moment(new Date()).format("DDMMYYHHmmss")}.xlsx`,
  );
};

/**
 * Converts a data URI to a Blob object.
 *
 * @param {string} dataURI - The data URI to convert to a Blob.
 * @returns {Blob} The Blob object converted from the data URI.
 */
export function dataURItoBlob(dataURI) {
  const byteString = atob(dataURI.split(",")[1]);
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
}

/**
 * Asynchronous function to retrieve the balance details for a specific channel and project.
 *
 * @param {string} channel_id - The ID of the channel to retrieve the balance for.
 * @param {string} project_id - The ID of the project to retrieve the balance for.
 * @returns {Object} An object containing the ewallet balance, deposit balance, and transit balance.
 * @throws {Error} If an error occurs during the balance retrieval process.
 */
export const GetBalance = async (channel_id, project_id) => {
  let projects;
  try {
    if (!project_id) {
      projects = await axios.get(
        `${
          process.env.REACT_APP_BASE_URL_API
        }/v2/collection/project/data?channel_id=${channel_id ?? ""}`,
        { headers: HEADER },
      );

      projects = projects.data.data
        .map((_project) => _project.project_id)
        .toString();
    }

    let balance = await axios.get(
      `${
        process.env.REACT_APP_BASE_URL_API
      }/v2/collection/mitra/ewallet/detail/${channel_id}?project_id=${
        project_id ?? projects
      }`,
      {
        headers: HEADER,
      },
    );
    return {
      ewallet_balance: balance.data.data.ewallet_balance,
      deposit_balance: balance.data.data.deposit_balance,
      transit_balance: balance.data.data.transit_balance,
    };
  } catch (error) {
    console.error(error);
  }
};

/**
 * Fetches data options asynchronously and updates the options state.
 * @param {Array} data - An array of objects containing the `key` and `url` properties.
 * @param {Function} setOptions - A function to update the options state.
 */
export const GetDataOptions = async (data, setOptions) => {
  try {
    for (const detail of data) {
      setOptions((prevState) => ({
        ...prevState,
        [detail.key]: {
          data: prevState[detail.key]?.data ?? [],
          isLoading: true,
        },
      }));

      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API}${detail.url}`,
        {
          headers: HEADER,
        },
      );

      setOptions((prevState) => ({
        ...prevState,
        [detail.key]: {
          data: res.data.data.map((option) => ({
            ...option,
            label: option[detail.label],
            value: option[detail.key],
          })),
          isLoading: false,
        },
      }));
    }
  } catch (error) {
    console.error(error);
  }
};

export const sheetNotify = (data, setSheetDetail) => {
  setSheetDetail({
    title: data.title,
    detail: data.detail,
    open: true,
    icon: data.icon,
  });
};
